import { displayDigits } from '../../utils/displayDigits';
import React from 'react';
import {
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoTrophyOutline,
} from 'react-icons/io5';
import { useLongPress } from '../../hooks/useLongPress';
import type { CalendarEvent } from '../../services/api';
import triathlonHeroUrl from '@assets/calendar-event-triathlon-v1.jpg';

interface Props {
  event: CalendarEvent;
  future: boolean;
  untilEvent: string;
  monthLabel: string;
  dayLabel: string | number;
  onPress: () => void;
  onLongPress: () => void;
  dragProps?: {
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    'data-dragging'?: string;
  };
}

function isTriathlonEvent(event: CalendarEvent): boolean {
  const label = `${event.name ?? ''} ${event.description ?? ''}`
    .toLocaleLowerCase()
    .replace(/\u200c/g, ' ');

  return (
    Number(event.sport_type_id) === 20 ||
    label.includes('triathlon') ||
    label.includes('سه گانه')
  );
}

/** Event row with long-press actions and optional desktop drag. */
export function CalendarEventRow({
  event,
  future,
  untilEvent,
  monthLabel,
  dayLabel,
  onPress,
  onLongPress,
  dragProps,
}: Props) {
  const { bind, wrapClick } = useLongPress(onLongPress);
  const triathlon = isTriathlonEvent(event);

  return (
    <button
      type="button"
      className={`calendar-event-row calendar-event-row--hero${future ? '' : ' calendar-event-row--past'}${dragProps?.['data-dragging'] === 'true' ? ' calendar-event-row--dragging' : ''}`}
      onClick={wrapClick(onPress)}
      dir="rtl"
      {...bind}
      {...dragProps}
    >
      {triathlon ? (
        <img
          src={triathlonHeroUrl}
          alt=""
          aria-hidden="true"
          className="calendar-event-row__hero-image"
        />
      ) : null}
      <span className="calendar-event-row__hero-shade" aria-hidden="true" />

      <div className="calendar-event-row__hero-content">
        <div className="calendar-event-row__hero-head">
          <span className="calendar-event-row__sport-badge">
            <IoTrophyOutline aria-hidden />
            {triathlon ? 'مسابقه سه‌گانه' : 'مسابقه'}
          </span>

          {monthLabel ? (
            <span className="calendar-event-row__date-badge">
              <small>{monthLabel}</small>
              <strong>{displayDigits(dayLabel)}</strong>
            </span>
          ) : (
            <IoCalendarOutline
              className="calendar-event-row__calendar-icon"
              aria-hidden
            />
          )}
        </div>

        <div className="calendar-event-row__text">
          <strong>{displayDigits(event.name)}</strong>
          <span className="calendar-event-row__status">
            {future ? <IoTimeOutline aria-hidden /> : <IoCheckmarkCircleOutline aria-hidden />}
            {displayDigits(untilEvent)}
          </span>
        </div>
      </div>
    </button>
  );
}
